import { Button, Tooltip } from "@braintrust/braintrust-ui-components";
import { useUserNeedsIdentityVerification } from "@js/apps/identity-verification";
import type { FreelancerOffer } from "@js/types/jobs";
import { assertUnreachable } from "@js/utils";

import { offerRequiresAcceptance } from "../../utils";
import { DownloadOfferButton } from "../download-offer-button";

import { MessageButton } from "./message-button";

export type TalentOfferCTAButtonsProps = {
  offer: FreelancerOffer;
  onOfferReject: () => void;
};

export const TalentOfferCTAButtons = ({
  offer,
  onOfferReject,
}: TalentOfferCTAButtonsProps) => {
  const { userFailedIdentityVerification } = useUserNeedsIdentityVerification();
  const interlocutorId = offer.job.creator.id;

  switch (offer.status) {
    case ENUMS.OfferStatus.ACCEPTED:
    case ENUMS.OfferStatus.CANCELLED:
    case ENUMS.OfferStatus.REJECTED:
    case ENUMS.OfferStatus.EXPIRED:
      return (
        <>
          <DownloadOfferButton offer={offer} />
          <MessageButton interlocutor={interlocutorId} />
        </>
      );
    case ENUMS.OfferStatus.EXTENDED:
      return (
        <>
          <DownloadOfferButton offer={offer} />
          <MessageButton interlocutor={interlocutorId} />
          {offerRequiresAcceptance(offer) && (
            <AcceptOfferButton
              userFailedIdentityVerification={userFailedIdentityVerification}
            />
          )}
        </>
      );

    case ENUMS.OfferStatus.NEW:
    case ENUMS.OfferStatus.UPDATED:
      return (
        <>
          <DownloadOfferButton offer={offer} />
          <MessageButton interlocutor={interlocutorId} />
          <Button
            variant="destructive"
            shape="squared"
            size="medium"
            onClick={() => onOfferReject()}
          >
            Reject
          </Button>
          <AcceptOfferButton
            userFailedIdentityVerification={userFailedIdentityVerification}
          />
        </>
      );

    default:
      assertUnreachable(offer.status);
      return null;
  }
};

const AcceptOfferButton = ({
  userFailedIdentityVerification,
}: {
  userFailedIdentityVerification: boolean;
}) => {
  return (
    <Tooltip
      title={
        userFailedIdentityVerification
          ? "Due to ID verification failure"
          : undefined
      }
      disabled={!userFailedIdentityVerification}
    >
      <span>
        <Button
          variant="positive"
          shape="squared"
          type="submit"
          size="medium"
          disabled={userFailedIdentityVerification}
        >
          Accept offer
        </Button>
      </span>
    </Tooltip>
  );
};
